// import Apple from "@/assets/icons/Apple.svg";
import GoogleIcon from "@/assets/icons/Google.svg";
import { CustomButton, IconButton } from "@/components/ui/Buttons";
import HookFormInputField from "@/components/form/HookFormInputField";
import { FormProvider, useForm } from "react-hook-form";
// import { CustomCheckbox } from "@/components/ui/Checkbox";
import {
  Link,
  ScrollRestoration,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import {
  LoginFormValues,
  loginWithEmailSchema,
} from "@/validation/form.schema";
import { useLoginMutation } from "@/redux/feature/auth/authApi";
import { useAppDispatch } from "@/redux/hooks/hooks";
import { useState } from "react";
import { VerifyToken } from "@/utils/verifyToken";
import { setUser } from "@/redux/feature/auth/authSlice";
import toast from "react-hot-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import logo from "@/assets/logo/logo-icon.png";
import AuthContainer from "@/components/ui/authContainer";

const SignInV2 = () => {
  const dispatch = useAppDispatch();
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerms] = useSearchParams();
  const userExists = searchTerms.get("userNotExist");
  const clientAssociated = searchTerms.get("clientAssociated");
  const [login] = useLoginMutation();
  const methods = useForm<LoginFormValues>({
    resolver: zodResolver(loginWithEmailSchema),
  });

  const navigate = useNavigate();

  const onSubmit = async (data: LoginFormValues) => {
    try {
      setLoading(true);

      const payload = {
        email: data.email.trim().toLowerCase(),
        password: data.password,
      };

      const res = await login(payload).unwrap();

      toast.success("Successfully Logged In");
      const user = VerifyToken(res.data.accessToken);
      dispatch(setUser({ user: user, token: res.data.accessToken }));
      navigate("/dashboard");
    } catch (err) {
      setLoading(false);
      toast.error("Something Went Wrong");

      const error = err as { data?: { errorSource?: { message: string }[] } };

      if (error?.data) {
        error?.data?.errorSource?.forEach((item) => {
          toast.error(item.message);
          setError(item.message);
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const api_baseUrl =
        import.meta.env.VITE_NODE_ENV === "development"
          ? import.meta.env.VITE_API_BASE_URL_LOCAL
          : import.meta.env.VITE_NODE_ENV === "stage"
            ? import.meta.env.VITE_API_BASE_URL_STAGE
            : import.meta.env.VITE_NODE_ENV === "dev"
              ? import.meta.env.VITE_API_BASE_URL_DEV
              : import.meta.env.VITE_API_BASE_URL_PROD;

      const redirectUrl = `${window.location.origin}/auth/verify`;
      const base_url = `${window.location.origin}`;

      window.location.href = `${api_baseUrl}/auth/google-oauth?redirectTo=${encodeURIComponent(
        redirectUrl,
      )}&baseUrl=${encodeURIComponent(base_url)}&state=creator_login`;
    } catch (error) {
      console.error("Error during Google sign-in:", error);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen px-4 lg:px-0">
      <section className="border border-[#D8D8DD] w-full max-w-[575px] p-4 sm:p-6 rounded-2xl bg-white">
        <div className="border-[1.5px] rounded-lg border-gray-300 p-1 w-[40px]">
          <img src={logo} className="w-full" alt="Logo" />
        </div>
        <h2 className="text-gray-900 pb-2 text-heading-4 font-semibold mt-3">
          Login
        </h2>
        <p className="text-gray-500 select-none text-caption-1-large">
          Welcome back! Enter the details.
        </p>

        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="mt-8 space-y-5"
          >
            <HookFormInputField
              placeholder="josephwilliams@gmail.com"
              name="email"
              label="Email"
            />

            <HookFormInputField
              placeholder="Enter password"
              name="password"
              label="Password"
              type="password"
            />

            <div className="flex justify-end">
              <Link
                className="text-sm font-semibold text-[#FF6600]"
                to="/auth/forgot-password"
              >
                Forgot password?
              </Link>
            </div>

            {userExists === "true" && (
              <p className="text-sm text-red-600 leading-[14px]">
                User does not exist. Please create an account first.
              </p>
            )}

            {clientAssociated === "true" && (
              <p className="text-sm text-red-600 leading-[14px]">
                This email is already associated with a client account.
              </p>
            )}

            {/* Primary Button (Matched Style) */}
            <CustomButton
              backgroundColor="#FF6600"
              color="white"
              padding="10px"
              fontSize="16px"
              fontWeight="550"
              borderRadius="200px"
              className="w-full auth-btn-shadow"
              type="submit"
              disabled={isLoading}
            >
              {isLoading ? "Logging in..." : "Log In"}
            </CustomButton>

            {/* Google Button (Matched Style) */}
            <CustomButton
              variant="outline"
              color="#344054"
              padding="10px"
              fontSize="16px"
              fontWeight="550"
              borderRadius="200px"
              border="1px solid #D0D5DD"
              className="w-full flex justify-center items-center gap-x-3 auth-btn-shadow"
              onClick={handleGoogleSignIn}
              disabled={isLoading}
            >
              <GoogleIcon />
              <span>Sign in with Google</span>
            </CustomButton>
          </form>
        </FormProvider>

        <div className="flex items-center gap-x-1 mt-8 justify-center">
          <p className="text-[#475467] text-sm tracking-normal">
            Don’t have an account?
          </p>
          <Link
            to={"/auth/sign-up"}
            className="text-[#FF6600] text-sm tracking-normal font-semibold"
          >
            Create Account
          </Link>
        </div>

        <ScrollRestoration />
      </section>
    </div>
  );
};

export default SignInV2;
